import { useEffect, useRef, useState } from "react";
import FAQPreview from "../../../components/common/FAQPreview";
import { useContentHeightCalculation } from "../../../hooks/useContentHeightCalculation";
import FiEcosytem from "./common/FiEcosystem";
import FiInternalHeader from "./common/FiInternalHeader";
import FiSubscriptionPlans from "./common/FiSubscriptionPlans";
import { getSubscriptionFeatureDetails } from "../../../api/payments/subscriptionsAPI";
import LoadingPlaceholder from "../../../components/ui/loading/LoadingPlaceholder";
import { useSelector } from "react-redux";
import { getLanguageContent } from "../../../utils/language/languageSelector";
import { INSIGHTS_HUB_SCHEMA } from "../../../utils/schemas/language/pages/insightsHubSchema";
import MoneyGuardian from "./MoneyGuardian";
import CollapsibleCardContainer from "../../../components/ui/accordions/CollapsibleCardContainer";
import UpgradePlanPopup from "../dashboards/finances-dashboard/personal-finances/common/UpgradePlanPopup";
import Reports from "./reports/Reports";

function FinancialInsightsHome() {
  const content = getLanguageContent(INSIGHTS_HUB_SCHEMA);
  const [featureData, setFeatureData] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(true);

  const [isUpgradePopupOpen, setIsUpgradePopupOpen] = useState(false);

  const { contentRef, mainRef } = useContentHeightCalculation();

  const userState = useSelector((state) => state.auth.user);
  const financialInsights = userState?.financial_insights;
  const isSubscribed = financialInsights?.is_subscribed;

  const plansSectionRef = useRef(null);

  useEffect(() => {
    const loadPricingMatrix = async () => {
      try {
        const response = await getSubscriptionFeatureDetails(
          "financial_insights",
        );

        if (response.ok) {
          const data = await response.json();
          setFeatureData(data.subscription || null);
        } else {
          console.error(
            "Pricing channel returned a bad operational status code.",
          );
        }
      } catch (err) {
        console.error(
          "Error communicating with subscription feature details endpoint:",
          err,
        );
      } finally {
        setLoadingPricing(false);
      }
    };

    loadPricingMatrix();
  }, []);

  const handleScrollToPlans = () => {
    if (plansSectionRef.current) {
      const elementPosition =
        plansSectionRef.current.getBoundingClientRect().top;
      const absolutePosition = elementPosition + window.pageYOffset;
      const offsetPosition = absolutePosition - 90;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="main-content-area" ref={mainRef}>
      <div className="content" ref={contentRef}>
        {loadingPricing ? (
          <div style={{ padding: "32px" }}>
            <LoadingPlaceholder rows={4} type="card-large" />
          </div>
        ) : (
          <>
            {isSubscribed ? (
              <div className="internal-content-body gap-reset">
                <CollapsibleCardContainer
                  title={content.subscriber_view.money_guardian.title}
                  defaultOpen={true}
                  gap="20px"
                  mode="page"
                >
                  {/* Hand down the show popup click handler seamlessly */}
                  <MoneyGuardian
                    content={content}
                    onUpgradeClick={() => setIsUpgradePopupOpen(true)}
                  />
                </CollapsibleCardContainer>
                <CollapsibleCardContainer
                  title={content.subscriber_view.reports.title}
                  gap="20px"
                  mode="page"
                >
                  <Reports
                    content={content}
                    isPaidPlan={
                      userState?.financial_insights?.plan_code !== "fi_free"
                    }
                    onUpgradeClick={() => setIsUpgradePopupOpen(true)}
                  />
                </CollapsibleCardContainer>
              </div>
            ) : (
              <>
                <div className="internal-content-body padding-reset-mobile">
                  <FiInternalHeader
                    onSeePlansClick={handleScrollToPlans}
                    content={{
                      hero: content.hero,
                      value_cards: content.value_cards,
                    }}
                  />
                </div>
                <div className="internal-content-body">
                  <FiEcosytem content={content} />
                  <FiSubscriptionPlans
                    sectionRef={plansSectionRef}
                    featureData={featureData}
                    content={content}
                  />
                  <FAQPreview
                    title={content.faq.title}
                    faqs={content.faq.items}
                    more={false}
                    maxWidthCard={true}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Render the Upgrade Plan Popup at the parent root level */}
      <UpgradePlanPopup
        isOpen={isUpgradePopupOpen}
        onClose={() => setIsUpgradePopupOpen(false)}
        featureData={featureData}
        content={content}
        currentSubscriptionUuid={financialInsights?.subscription_uuid}
      />
    </div>
  );
}

export default FinancialInsightsHome;
